import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { provideEffects } from '@ngrx/effects';
import { provideState } from '@ngrx/store';

import { AccountPageComponent } from './account-page/account-page.component';
import { ActOnComponent } from './act-on/act-on.component';
import { AppMainComponent } from './app.main.component';
import { AssetsComponent } from './assets/assets.component';
import { AvailabilityComponent } from './availability/availability.component';
import { BlueSageQueueComponent } from './bluesage/bluesage-queue.component';
import * as blueSageQueueEffects from './bluesage/bluesage-queue.effects';
import { blueSageQueueFeature } from './bluesage/bluesage-queue.feature';
import * as blueSageSettingsEffects from './bluesage/bluesage-settings.effects';
import { blueSageSettingsFeature } from './bluesage/bluesage-settings.feature';
import * as blueSageRefundDialogEffects from './bluesage/refund-dialog/bluesage-refund-dialog.effects';
import { blueSageRefundDialogFeature } from './bluesage/refund-dialog/bluesage-refund-dialog.feature';
import { BusinessUnitsComponent } from './business-units/business-units.component';
import { DASHBOARDS, getDashboardPermissions } from './dashboardv2/dashboards-list';
import { Dashboardv2Component } from './dashboardv2/dashboardv2.component';
import { DiscountsComponent } from './discounts/discounts.component';
import { PreviewDynamicReportComponent } from './dynamic-reports/preview-dynamic-report/preview-dynamic-report.component';
import { AccountingComponent } from './franchise/accounting/accounting.component';
import { BusinessComponent } from './franchise/business/business.component';
import { BusinessGeneralComponent } from './franchise/business-general/business-general.component';
import { DocumentSettingsComponent } from './franchise/document-settings/document-settings.component';
import { FranchiseInventoryItemsComponent } from './franchise/franchise-inventory-items/franchise-inventory-items.component';
import { RulesSettingsComponent } from './franchise/rules-settings/rules-settings.component';
import { ScriptsSettingsComponent } from './franchise/scripts-settings/scripts-settings.component';
import { TaxesComponent } from './franchise/taxes/taxes.component';
import { ZoneAreasComponent } from './franchise/zone-areas/zone-areas.component';
import { ZONE_TYPES } from './global.constants';
import { HistorySearchComponent } from './history-search/history-search.component';
import { HomeComponent } from './home/home.component';
import { InvoicesComponent } from './invoices/invoices.component';

import * as AddDiscountsEffects from './job/add-discounts-modal/add-discounts-modal.effects';
import { addDiscountsModalFeature } from './job/add-discounts-modal/add-discounts-modal.feature';
import * as ActivityEffects from './job/job-activity/job-activity.effects';
import * as JobCreateCustomerEffects from './job/job-create/job-create-customer/job-create-customer.effects';
import { jobCreateCustomerFeature } from './job/job-create/job-create-customer/job-create-customer.feature';
import * as JobCreateLocationsEffects from './job/job-create/job-create-locations/job-create-locations-state.effects';
import { jobCreateSummaryFeature } from './job/job-create/job-create-summary/job-create-summary.feature';
import * as EstimateEffects from './job/job-estimate/job-estimate.effects';
import { JobFilesComponent } from './job/job-files/job-files.component';
import * as DocumentsEffects from './job/job-files/job-files.effects';
import { JobFinancialsComponent } from './job/job-financials/job-financials.component';
import * as FinancialsEffects from './job/job-financials/job-financials.effects';
import * as JobHeaderEffects from './job/job-header/job-header.effects';
import * as JobStatusDropdownEffects from './job/job-header/job-status-dropdown/job-status-dropdown.effects';
import * as JobTagsEffects from './job/job-header/job-tags/job-tags.effects';
import { jobTagsFeature } from './job/job-header/job-tags/job-tags.feature';
import { JobInventoryComponent } from './job/job-inventory/job-inventory.component';
import * as InventoryEffects from './job/job-inventory/job-inventory.effects';
import { jobInventoryFeature } from './job/job-inventory/job-inventory.feature';
import * as InvoiceEffects from './job/job-invoice/job-invoice.effects';
import { JobNavigatorPageComponent } from './job/job-navigator/job-navigator-page.component';
import * as jobNavigatorEffects from './job/job-navigator/job-navigator.effects';
import { JobOverviewComponent } from './job/job-overview/job-overview.component';
import * as JobAgentEffects from './job/job-overview/overview-header/job-agent/job-agent.effects';
import * as OverviewLocationsEffects from './job/job-overview/overview-locations/overview-locations.effects';
import { overviewLocationsFeature } from './job/job-overview/overview-locations/overview-locations.feature';
import * as OverviewTimelineEffects from './job/job-overview/overview-timeline/overview-timeline.effects';
import { overviewTimelineFeature } from './job/job-overview/overview-timeline/overview-timeline.feature';
import * as EventItineraryEffects from './job/job-overview/overview-timeline/timeline-book-event/event-itinerary/event-itinerary.effects';
import { eventItineraryFeature } from './job/job-overview/overview-timeline/timeline-book-event/event-itinerary/event-itinerary.feature';
import { timelineBookEventFeature } from './job/job-overview/overview-timeline/timeline-book-event/timeline-book-event.feature';
import * as BookEventDialogEffects from './job/job-overview/overview-timeline/timeline-book-event/timeline-book-events.effects';
import * as JobCreateEventDialogEffects from './job/job-overview/overview-timeline/timeline-create-event/timeline-create-event.effects';
import * as JobToolSubscriptionEffects from './job/job-state/job-tool-subscription.effects';
import * as jobToolEffects from './job/job-tool.effects';
import { JobWorkordersComponent } from './job/job-workorders/job-workorders.component';
import * as WorkordersEffects from './job/job-workorders/job-workorders.effects';
import { jobWorkordersFeature } from './job/job-workorders/job-workorders.feature';
import { JobComponent } from './job/job.component';
import * as jobEffects from './job/job.effects';
import { LeaveJobGuard } from './job/leave-workorders.guard';
import * as MutateChargesEffects from './job/mutate-charges/mutate-charges.effects';
import { mutateObjectFeature } from './job/mutate-object/mutate-object.feature';
import { JobTabComponent } from './jobs/job-tab.component';
import { JobsPageComponent } from './jobs/jobs-page.component';
import { LegacyJobRouteRedirectGuard, legacyJobRouteMatcher } from './legacy-job-route-redirect.guard';
import { LoadingComponent } from './loading/loading.component';
import * as leadSourceEffects from './marketing/lead-source-settings/lead-source-store/lead-source.effects';
import { LeadSourceFeature } from './marketing/lead-source-settings/lead-source-store/lead-source.reducers';
import { MarketingComponent } from './marketing/marketing.component';
import { NeedsAttentionComponent } from './needs-attention/needs-attention.component';
import * as newOpportunityAddInventoryEffects from './new-opportunity/new-opportunity-add-inventory/new-opportunity-add-inventory.effects';
import { newOpportunityAddInventoryFeature } from './new-opportunity/new-opportunity-add-inventory/new-opportunity-add-inventory.feature';
import * as newOpportunitySearchCustomersDialogEffects from './new-opportunity/new-opportunity-search-customers-dialog/new-opportunity-search-customers-dialog.effects';
import { newOpportunitySearchCustomersDialogFeature } from './new-opportunity/new-opportunity-search-customers-dialog/new-opportunity-search-customers-dialog.feature';
import * as newOpportunitySimilarJobsEffects from './new-opportunity/new-opportunity-similar-jobs/new-opportunity-similar-jobs.effects';
import { newOpportunitySimilarJobsFeature } from './new-opportunity/new-opportunity-similar-jobs/new-opportunity-similar-jobs.feature';
import { NewOpportunityComponent } from './new-opportunity/new-opportunity.component';
import * as newOpportunityEffects from './new-opportunity/new-opportunity.effects';
import { newOpportunityFeature } from './new-opportunity/new-opportunity.feature';
import { NiceJobsComponent } from './nice-jobs/nice-jobs.component';
import { NotificationViewerComponent } from './notifications/notification-viewer/notification-viewer.component';
import { NotificationsComponent } from './notifications/notifications.component';
import { PageComponent } from './page/page.component';
import { AppAccessdeniedComponent } from './pages/app.accessdenied.component';
import { AppErrorComponent } from './pages/app.error.component';
import { AppNotfoundComponent } from './pages/app.notfound.component';
import { NoAvailableZonesComponent } from './pages/no-available-zones/no-available-zones.component';
import { PaymentRedirectComponent } from './payment/payment-redirect/payment-redirect.component';
import { PaymentComponent } from './payment/payment.component';
import { PaymentTypesComponent } from './payment-types/payment-types.component';
import { DISPATCH_PERMISSIONS, FRONTEND_PERMISSIONS } from './permissions.constants';
import { ProductsComponent } from './products/products.component';
import { QuickbooksDashboardComponent } from './quickbooks/quickbooks-dashboard/quickbooks-dashboard.component';
import { QuickbooksLoginComponent } from './quickbooks/quickbooks-login/quickbooks-login.component';
import { QuickbooksComponent } from './quickbooks/quickbooks.component';
import { DashboardsCardComponent } from './reports/dashboards-card/dashboards-card.component';
import { ReportPageComponent } from './reports/report-page/report-page.component';
import { ReportTypePageComponent } from './reports/report-type-page/report-type-page.component';
import { ReportTypesComponent } from './reports/report-types/report-types.component';
import { ScheduledReportPageComponent } from './reports/scheduled-report-page/scheduled-report-page.component';
import { RingcentralComponent } from './ringcentral/ringcentral.component';
import { RoutePermissionGuard } from './route-permission.guard';
import * as BookingScheduleEffects from './schedules/booking-schedule/booking-schedule.effects';
import { bookingScheduleFeature } from './schedules/booking-schedule/booking-schedule.feature';
import * as dispatchCalendarRemarksDialogEffects from './schedules/dispatch/components/dispatch-calendar-remarks-dialog/dispatch-calendar-remarks-dialog.effects';
import { dispatchCalendarRemarksDialogFeature } from './schedules/dispatch/components/dispatch-calendar-remarks-dialog/dispatch-calendar-remarks-dialog.feature';
import { DispatchComponent } from './schedules/dispatch/dispatch.component';
import * as dispatchEffects from './schedules/dispatch/store/dispatch.effects';
import { DispatchFeature } from './schedules/dispatch/store/dispatch.reducer';
import { LeaveScheduleGuard } from './schedules/leave-schedule.guard';
import { SchedulePageComponent } from './schedules/schedule-page/schedule-page.component';
import { SchedulesComponent } from './schedules/schedules.component';
import { SearchResultsComponent } from './search-results/search-results.component';
import { CommunicationsComponent } from './settings/communications/communications.component';
import { ConfigComponent } from './settings/config.component';
import { SetUrlZoneGuard } from './shared/guards/set-url-zone.guard';
import { locationDetailsWizardFeature } from './shared/location-details-wizard/location-details-wizard.feature';
import { icalStateEffects } from './shared/mutate-ical-token/ical.state';
import * as RightPanelEffects from './shared/right-panel/right-panel.effects';
import * as templatesEffects from './templates/state/templates.effects';
import { TemplatesComponent } from './templates/templates.component';
import { UploadGuard } from './upload.guard';
import { UserAuthGuard } from './user-auth.guard';
import { PeopleCrewComponent } from './users/people-crew/people-crew.component';
import { PeopleCustomersComponent } from './users/people-customers/people-customers.component';
import { PeopleEmployeesComponent } from './users/people-employees/people-employees.component';
import * as userPageMergeConflictDialogEffects from './users/user-page/user-page-merge-conflict-dialog/user-page-merge-conflict-dialog.effects';
import { userPageMergeConflictDialogFeature } from './users/user-page/user-page-merge-conflict-dialog/user-page-merge-conflict-dialog.feature';
import * as userPageSimilarCustomersEffects from './users/user-page/user-page-similar-customers/user-page-similar-customers.effects';
import { userPageSimilarCustomersFeature } from './users/user-page/user-page-similar-customers/user-page-similar-customers.feature';
import { UserPageComponent } from './users/user-page/user-page.component';
import { UsersComponent } from './users/users.component';
import { UUID_REGEX } from './utilities/generic.util';
import { YelpComponent } from './yelp/yelp.component';
import { ZoneTypeGuard } from './zone-type.guard';

// Exported (#7275): the app-routing wiring spec asserts the unsaved-edits
// guard sits on the ':viewType' child (SchedulesComponent owns the edit
// state), not on the /schedule parent.
export const routes: Routes = [
    {
        path: 'auth',
        loadChildren: () => import('../app/authentication/authentication.module').then(m => m.AuthenticationModule),
    },
    {
        path: 'register',
        redirectTo: 'auth/signup',
        pathMatch: 'full'
    },
    {
        path: '', component: AppMainComponent,
        canActivate: [SetUrlZoneGuard, UserAuthGuard],
        canActivateChild: [SetUrlZoneGuard, UserAuthGuard],
        data: {
            helpId: 'default',
        },
        providers: [
            provideEffects(
                RightPanelEffects,
            )
        ],
        children: [
            {
                path: '',
                redirectTo: 'home',
                pathMatch: 'full',
            },
            {
                path: 'home',
                component: HomeComponent,
            },
            {
                path: 'search',
                component: SearchResultsComponent,
                data: { customTitle: 'Search Results' },
            },
            {
                // Requirement: /dashboard redirects to /reports/dashboards/revenue.
                // The old dashboard route is kept for backwards-compatible bookmarks and
                // will eventually be replaced with a "For You" page.
                path: 'dashboard',
                redirectTo: 'reports/dashboards/revenue',
                pathMatch: 'full',
            },
            {
                path: 'dashboard/:tab',
                redirectTo: 'reports/dashboards/:tab',
            },
            { path: 'account', component: AccountPageComponent },

            // {path: 'corporate', component: CorporateComponent},
            // {
            //     path: 'franchise-partners',
            //     component: FranchisePartnersComponent,
            //     canActivate: [RoutePermissionGuard],
            //     data: { permissions: PERMISSIONS.frontend.fr }
            // },
            {
                path: 'schedule',
                component: SchedulePageComponent,
                canActivate: [RoutePermissionGuard],
                // #7275: canDeactivate moved to the ':viewType' child route.
                // The guard reads SchedulesComponent.latestEventEdits; on the
                // parent route its component (SchedulePageComponent) has no
                // such state, so the guard passed silently.
                providers: [
                    provideState(dispatchCalendarRemarksDialogFeature),
                    provideEffects(
                        icalStateEffects,
                        dispatchCalendarRemarksDialogEffects,
                    )
                ],
                data: {
                    permissions: FRONTEND_PERMISSIONS.schedule,
                    helpId: 'schedule',
                },
                children: [
                    {
                        path: '',
                        redirectTo: 'day',
                        pathMatch: 'full',
                    },
                    /*
                    Ensure that 'dispatch' is the first child route and the second is the default view.
                    This will ensure that the 'dispatch' page is matched first. If the view type is not 'dispatch',
                    the Angular router will load the default view.
                    */

                    {
                        path: 'dispatch',
                        component: DispatchComponent,
                        canActivate: [RoutePermissionGuard],
                        providers: [
                            provideState(DispatchFeature),
                            provideEffects(
                                dispatchEffects,
                            )
                        ],
                        data: {
                            permissions: DISPATCH_PERMISSIONS.viewDispatch,
                            customTitle: 'from-component'
                        }
                    },
                    {
                        path: 'map',
                        loadComponent: () => import('./schedules/schedule-map/schedule-map.component').then(m => m.ScheduleMapComponent),
                        canActivate: [RoutePermissionGuard],
                        providers: [
                            provideState(DispatchFeature),
                            provideEffects(
                                dispatchEffects,
                            )
                        ],
                        data: {
                            permissions: DISPATCH_PERMISSIONS.viewMap,
                            customTitle: 'Map View'
                        }
                    },
                    {
                        // Ops Readiness (Maria's ops dashboard cloned to the
                        // schedule page — issue: schedule-page tools). Reads
                        // its date window from the route (&date= / &dateTo=).
                        path: 'ops-readiness',
                        loadComponent: () =>
                            import('./schedules/ops-readiness/ops-readiness.component')
                                .then(m => m.ScheduleOpsReadinessComponent),
                        canActivate: [RoutePermissionGuard],
                        data: {
                            permissions: FRONTEND_PERMISSIONS.dashboardOpsReadiness,
                        }
                    },
                    {
                        path: ':viewType',
                        component: SchedulesComponent,
                        // #7275: edits live on SchedulesComponent (latestEventEdits)
                        // — guard THIS child (not the /schedule parent) so
                        // Schedule<->Dispatch<->Map tab switches and leaving
                        // /schedule with unsaved edits warn.
                        canDeactivate: [LeaveScheduleGuard],
                    },
                ],

            },
            {
                path: 'people',
                component: UsersComponent,
                canActivate: [RoutePermissionGuard],
                data: {
                    permissions: FRONTEND_PERMISSIONS.users,
                    helpId: 'users',
                },
                children: [
                    {
                        path: '',
                        redirectTo: 'customers',
                        pathMatch: 'full'
                    },
                    {
                        path: 'customers',
                        component: PeopleCustomersComponent
                    },
                    {
                        path: 'employees',
                        component: PeopleEmployeesComponent
                    },
                    {
                        path: 'crew',
                        component: PeopleCrewComponent
                    }
                ]
            },
            // {
            //     path: 'invoices',
            //     component: InvoicesComponent,
            //     canActivate: [RoutePermissionGuard],
            //     data: { permissions: FRONTEND_PERMISSIONS.in }
            // },
            {
                path: 'notifications',
                component: NotificationsComponent,
                canActivate: [RoutePermissionGuard],
                data: { permissions: FRONTEND_PERMISSIONS.notifications },
                children: [
                    {
                        path: ':notificationId',
                        component: NotificationViewerComponent,
                        canActivate: [RoutePermissionGuard],
                        data: { permissions: FRONTEND_PERMISSIONS.notifications }
                    }
                ]
            },
            {
                path: 'reports/dynamic/new',
                component: PreviewDynamicReportComponent,
                canActivate: [RoutePermissionGuard],
                data: { permissions: FRONTEND_PERMISSIONS.reports },
            },
            {
                // Requirement: Legacy redirect — /reports/:reportId was the old
                // URL for static reports. Only UUIDs are redirected, so folder names
                // (including numeric ones like /reports/5020) fall through to the
                // folder matcher below.
                matcher: (segments) => {
                    if (segments.length === 2 && segments[0].path === 'reports'
                        && UUID_REGEX.test(segments[1].path)) {
                        return { consumed: segments, posParams: { reportId: segments[1] } };
                    }
                    return null;
                },
                redirectTo: 'reports/static/:reportId',
                pathMatch: 'full',
            },
            {
                matcher: (segments) => {
                    if (segments.length === 3 && segments[0].path === 'reports'
                        && UUID_REGEX.test(segments[1].path) && segments[2].path === 'data') {
                        return { consumed: segments, posParams: { reportId: segments[1] } };
                    }
                    return null;
                },
                redirectTo: 'reports/static/:reportId/data',
                pathMatch: 'full',
            },
            // Requirement: Folder-based routing — match /reports and /reports/<any depth>
            // so folder URLs like /reports/Static or /reports/MyFolder/SubFolder all
            // render ReportTypesComponent. Excludes reserved second-level segments
            // (dashboards, dynamic, static, type, scheduled) which have their own
            // dedicated routes below. Reserved names are matched by complete segment
            // so valid folders like "dynamic-metrics" or "dashboards-v2" still work.
            // A UrlMatcher is used instead of a wildcard path because Angular flat
            // routes don't support path: 'reports/**'.
            {
                matcher: (segments) => {
                    const reserved = new Set(['dashboards', 'dynamic', 'static', 'type', 'scheduled']);
                    if (segments.length >= 1 && segments[0].path === 'reports'
                        && !(segments.length >= 2 && reserved.has(segments[1].path))
                    ) {
                        return { consumed: segments };
                    }
                    return null;
                },
                component: ReportTypesComponent,
                canActivate: [RoutePermissionGuard],
                data: { permissions: FRONTEND_PERMISSIONS.reports, helpId: 'reports' }
            },
            {
                // Requirement: /reports/dashboards — dashboards only (no reports list).
                path: 'reports/dashboards',
                component: DashboardsCardComponent,
                canActivate: [RoutePermissionGuard],
                data: { permissions: FRONTEND_PERMISSIONS.reports, helpId: 'dashboard' },
            },
            {
                // Requirement: Individual dashboard pages under /reports/dashboards/<slug>.
                // The Dashboardv2Component hosts the period selector + breadcrumb + router-outlet.
                // The host guard ensures FRONTEND_PERMISSIONS.reports is enforced even for
                // dashboards (e.g. revenue) that have no entry-specific permissions.
                path: 'reports/dashboards',
                component: Dashboardv2Component,
                canActivate: [RoutePermissionGuard],
                data: { permissions: FRONTEND_PERMISSIONS.reports, customTitle: 'Dashboard', helpId: 'dashboard' },
                children: DASHBOARDS.map((d) => {
                    const perms = getDashboardPermissions(d);
                    return {
                        path: d.slug,
                        component: d.component,
                        data: {
                            ...(perms.length ? { permissions: perms } : {}),
                            helpId: d.helpId,
                            display: d.display ?? {},
                            dashboardSlug: d.slug,
                        },
                        ...(perms.length ? {
                            canActivate: [RoutePermissionGuard],
                        } : {}),
                    };
                }),
            },
            {
                path: 'reports/dynamic/:reportId',
                component: PreviewDynamicReportComponent,
                canActivate: [RoutePermissionGuard],
                data: { permissions: FRONTEND_PERMISSIONS.reports },
            },
            {
                path: 'reports/type/:reportTypeId',
                component: ReportTypePageComponent,
                canActivate: [RoutePermissionGuard],
                data: { permissions: FRONTEND_PERMISSIONS.reports, helpId: 'reports' }
            },
            {
                path: 'reports/scheduled/:scheduledReportId',
                component: ScheduledReportPageComponent,
                canActivate: [RoutePermissionGuard],
                data: { permissions: FRONTEND_PERMISSIONS.reports, helpId: 'reports' }
            },
            {
                path: 'reports/static/:reportId',
                component: ReportPageComponent,
                canActivate: [RoutePermissionGuard],
                data: { permissions: FRONTEND_PERMISSIONS.reports, helpId: 'reports' }
            },
            {
                path: 'reports/static/:reportId/:tab',
                component: ReportPageComponent,
                canActivate: [RoutePermissionGuard],
                data: { permissions: FRONTEND_PERMISSIONS.reports, helpId: 'reports' }
            },
            {
                path: 'loading',
                component: LoadingComponent,
            },
            {
                matcher: legacyJobRouteMatcher,
                canMatch: [LegacyJobRouteRedirectGuard],
                component: LoadingComponent,
            },
            {
                // Requirement: The jobs route must provide a default tab (recent)
                // plus seven business-specific job filters (open, closed, open-leads,
                // open-estimates, open-bookings, open-invoices, pending). Each tab passes
                // a filter object to JobTabComponent that controls the job list query.
                // Adding or removing tabs here changes the job-list routing semantics.
                path: 'jobs',
                component: JobsPageComponent,
                canActivate: [RoutePermissionGuard],
                data: {
                    permissions: FRONTEND_PERMISSIONS.jobs,
                    helpId: 'jobs'
                },
                children: [
                    {
                        path: '',
                        redirectTo: 'recent',
                        pathMatch: 'full',
                    },
                    {
                        path: 'recent',
                        component: JobTabComponent,
                        data: { filter: {}, title: 'Recently Created' },
                    },
                    {
                        path: 'open',
                        component: JobTabComponent,
                        data: { filter: { getJobsOfState: ['open'] }, title: 'Open Jobs' },
                    },
                    {
                        path: 'closed',
                        component: JobTabComponent,
                        data: { filter: { getJobsOfState: ['closed'] }, title: 'Closed Jobs' },
                    },
                    {
                        path: 'open-leads',
                        component: JobTabComponent,
                        data: { filter: { stage: 'lead', getJobsOfState: ['open'] }, title: 'Open Leads' },
                    },
                    {
                        path: 'open-estimates',
                        component: JobTabComponent,
                        data: { filter: { stage: 'estimate', getJobsOfState: ['open'] }, title: 'Open Estimates' },
                    },
                    {
                        path: 'open-bookings',
                        component: JobTabComponent,
                        data: { filter: { stage: 'booking', getJobsOfState: ['open'] }, title: 'Open Bookings' },
                    },
                    {
                        path: 'open-invoices',
                        component: JobTabComponent,
                        data: { filter: { stage: 'invoice', getJobsOfState: ['open'] }, title: 'Open Invoices' },
                    },
                    {
                        path: 'pending',
                        component: JobTabComponent,
                        // Requirement: The pending tab lists open jobs tagged
                        // "Approval Required". The tag is named rather than identified
                        // because tag ids differ per zone, so a shared route cannot
                        // hardcode one; `tags` is therefore a sibling of `filter` and not
                        // a key inside it, since JobsFilter accepts only tagIds. See
                        // tagNames on JobsComponent for where the name is resolved.
                        data: { filter: { getJobsOfState: ['open'] }, tags: 'approval required', title: 'Pending' },
                    },
                ],
            },
            {
                path: 'jobs',
                canActivate: [RoutePermissionGuard],
                data: {
                    permissions: FRONTEND_PERMISSIONS.jobs,
                    helpId: 'jobs'
                },
                providers: [
                    provideState(newOpportunityFeature),
                    provideState(newOpportunitySimilarJobsFeature),
                    provideState(newOpportunityAddInventoryFeature),
                    provideState(newOpportunitySearchCustomersDialogFeature),
                    provideState(jobCreateCustomerFeature),
                    provideState(jobCreateSummaryFeature),
                    provideState(overviewLocationsFeature),
                    provideState(overviewTimelineFeature),
                    provideState(locationDetailsWizardFeature),
                    provideState(timelineBookEventFeature),
                    provideState(bookingScheduleFeature),
                    provideState(jobTagsFeature),
                    provideState(jobInventoryFeature),
                    provideState(jobWorkordersFeature),
                    provideState(eventItineraryFeature),
                    provideState(addDiscountsModalFeature),
                    provideState(mutateObjectFeature),
                    provideEffects(
                      jobEffects,
                      newOpportunityEffects,
                      newOpportunitySimilarJobsEffects,
                      newOpportunityAddInventoryEffects,
                      newOpportunitySearchCustomersDialogEffects,
                      JobCreateCustomerEffects,
                      OverviewTimelineEffects,
                      OverviewLocationsEffects,
                      BookEventDialogEffects,
                      EventItineraryEffects,
                      InventoryEffects,
                      WorkordersEffects,
                      BookingScheduleEffects,
                      MutateChargesEffects,
                      AddDiscountsEffects,
                      JobTagsEffects,
                    ),
                    // The Import banner on the New Opportunity page dispatches
                    // importJobButtonClicked. Only that one effect is registered:
                    // the rest of jobToolEffects belongs to the job page route and
                    // would fire unrelated queries here.
                    provideEffects({
                      importJobToCurrentZone: jobToolEffects.importJobToCurrentZone,
                    }),
                ],
                children: [
                    {
                        path: 'new',
                        component: NewOpportunityComponent,
                    },
                    {
                        path: ':jobId/edit',
                        component: NewOpportunityComponent,
                        canDeactivate: [LeaveJobGuard],
                    },
                ],
            },
            {
                path: 'jobs/:jobId',
                component: JobComponent,
                canActivate: [RoutePermissionGuard],
                canDeactivate: [LeaveJobGuard],
                data: {
                    permissions: FRONTEND_PERMISSIONS.jobs,
                    helpId: 'jobs'
                },
                providers: [
                    provideState(jobCreateCustomerFeature),
                    provideState(jobCreateSummaryFeature),
                    provideState(jobInventoryFeature),
                    provideState(overviewLocationsFeature),
                    provideState(addDiscountsModalFeature),
                    provideState(overviewTimelineFeature),
                    provideState(locationDetailsWizardFeature),
                    provideState(mutateObjectFeature),
                    provideState(timelineBookEventFeature),
                    provideState(bookingScheduleFeature),
                    provideState(jobTagsFeature),
                    provideState(jobWorkordersFeature),
                    provideState(DispatchFeature),
                    provideState(eventItineraryFeature),
                    // Provide here so unsaved changes banner may read control meta
                    provideState(newOpportunityFeature),
                    provideState(blueSageRefundDialogFeature),
                    provideEffects(
                      blueSageRefundDialogEffects,
                      jobEffects,
                      jobToolEffects,
                      JobCreateCustomerEffects,
                      JobCreateLocationsEffects,
                      JobCreateEventDialogEffects,
                      InventoryEffects,
                      WorkordersEffects,
                      MutateChargesEffects,
                      AddDiscountsEffects,
                      ActivityEffects,
                      JobAgentEffects,
                      OverviewLocationsEffects,
                      BookEventDialogEffects,
                      EventItineraryEffects,
                      JobToolSubscriptionEffects,
                      FinancialsEffects,
                      InvoiceEffects,
                      EstimateEffects,
                      JobHeaderEffects,
                      JobStatusDropdownEffects,
                      JobTagsEffects,
                      DocumentsEffects,
                      BookingScheduleEffects,
                      OverviewTimelineEffects,
                      // Requirement: #6142 - Only register the effects needed for crew assignment from workorders.
                      // The full dispatchEffects barrel includes effects that trigger unneeded GQL queries
                      // in this context (event listing, asset loading, book-off management, crew schedule emails, etc.).
                      {
                        GetRoles: dispatchEffects.GetRoles,
                        GetUsers: dispatchEffects.GetUsers,
                        LoadWorkerHistory: dispatchEffects.LoadWorkerHistory,
                        UpdateEventCrew: dispatchEffects.UpdateEventCrew,
                      },
                    ),
                ],
                children: [
                    {
                        path: '',
                        redirectTo: 'overview',
                        pathMatch: 'full',
                    },
                    {
                        path: 'overview',
                        component: JobOverviewComponent,
                    },
                    {
                        path: 'workorders',
                        component: JobWorkordersComponent,
                    },
                    {
                        path: 'navigator/:eventId',
                        component: JobNavigatorPageComponent,
                        canActivate: [UserAuthGuard],
                        // Route-scoped: the override effect only has meaning while a navigator is
                        // mounted, and scoping it here means it cannot fire for a job page that has
                        // no navigator open.
                        providers: [ provideEffects(jobNavigatorEffects) ],
                    },
                    {
                        path: 'billing',
                        component: JobFinancialsComponent,
                    },
                    {
                        path: 'inventory',
                        component: JobInventoryComponent,
                    },
                    {
                        path: 'documents',
                        component: JobFilesComponent,
                    }
                ],
            },
            {
                path: 'products',
                component: ProductsComponent,
                canActivate: [RoutePermissionGuard],
                data: {
                    permissions: FRONTEND_PERMISSIONS.products,
                    helpId: 'products'
                }
            },
            {
                path: 'assets',
                component: AssetsComponent,
                canActivate: [RoutePermissionGuard],
                data: {
                    permissions: FRONTEND_PERMISSIONS.assets,
                    helpId: 'assets'
                }
            },
            {
                path: 'needs-attention',
                component: NeedsAttentionComponent,
                canActivate: [RoutePermissionGuard],
                data: { permissions: FRONTEND_PERMISSIONS.needsAttention },
            },
            // {
            //     path: 'franchise',
            //     loadChildren: () => import('./franchise/franchise-management.module').then(m => m.FranchiseManagementModule)
            // },
            {
                // Requirement: The business parent route enforces FRONTEND_PERMISSIONS.franchise
                // via canActivate. canActivateChild runs RoutePermissionGuard (checking each
                // child's data.permissions) and ZoneTypeGuard (checking hiddenIfZoneType) on
                // every /business/* child route, so direct navigation to a child enforces the
                // same permission and zone-visibility checks as the icon grid. New child routes
                // must set data.permissions and data.hiddenIfZoneType to stay guarded.
                path: 'business',
                component: BusinessComponent,
                canActivate: [RoutePermissionGuard],
                canActivateChild: [RoutePermissionGuard, ZoneTypeGuard],
                data: {
                    permissions: FRONTEND_PERMISSIONS.franchise,
                    helpId: 'business'
                },
                children: [
                    {
                        path: 'documents',
                        component: DocumentSettingsComponent,
                        runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
                        data: {
                            permissions: FRONTEND_PERMISSIONS.franchise,
                            hiddenIfZoneType: [ZONE_TYPES.root],
                            helpId: 'documents',
                        },
                    },
                    {
                        path: 'general',
                        component: BusinessGeneralComponent,
                        runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
                        data: {
                            permissions: FRONTEND_PERMISSIONS.franchise,
                            helpId: 'business',
                            hiddenIfZoneType: [ZONE_TYPES.corporate, ZONE_TYPES.root],
                        },
                    },
                    {
                        path: 'units',
                        component: BusinessUnitsComponent,
                        runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
                        data: {
                            permissions: FRONTEND_PERMISSIONS.franchiseManagement,
                            hiddenIfZoneType: [ZONE_TYPES.franchise],
                            helpId: 'business-units'
                        },
                    },
                    {
                        path: 'areas',
                        component: ZoneAreasComponent,
                        runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
                        data: {
                            permissions: 'zones.view',
                            hiddenIfZoneType: [ZONE_TYPES.corporate, ZONE_TYPES.root],
                            helpId: 'areas'
                        },
                    },
                    {
                        path: 'rules',
                        component: RulesSettingsComponent,
                        runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
                        data: {
                            permissions: 'rules.view',
                            helpId: 'rules',
                        },
                    },
                    {
                        path: 'scripts',
                        component: ScriptsSettingsComponent,
                        runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
                        data: {
                            permissions: 'scripts.view',
                        },
                    },
                    {
                        path: 'taxes',
                        component: TaxesComponent,
                        runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
                        data: {
                            permissions: 'taxes.view',
                            hiddenIfZoneType: [ZONE_TYPES.corporate, ZONE_TYPES.root],
                            // TODO: write taxes help page
                            // helpId: 'taxes',
                        },
                    },
                    {
                        path: 'invoices',
                        component: InvoicesComponent,
                        runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
                    },
                    {
                        path: 'inventory',
                        component: FranchiseInventoryItemsComponent,
                        runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
                        data: {
                            permissions: FRONTEND_PERMISSIONS.franchise,
                            hiddenIfZoneType: [ZONE_TYPES.corporate, ZONE_TYPES.root],
                            // TODO: write inventory help page
                            // helpId: 'inventory',
                        },
                    },
                    {
                        path: 'availability',
                        component: AvailabilityComponent,
                        data: {
                            permissions: FRONTEND_PERMISSIONS.availability,
                            helpId: 'availability-schedule',
                        },
                    },
                    {
                        path: 'products',
                        component: ProductsComponent,
                        data: {
                            permissions: FRONTEND_PERMISSIONS.products,
                            helpId: 'products',
                        },
                    },
                    {
                        path: 'assets',
                        component: AssetsComponent,
                        data: {
                            permissions: FRONTEND_PERMISSIONS.assets,
                            // TO DO: Write assets help page
                            // helpId: 'assets',
                        },
                    },
                    {
                        path: 'marketing',
                        component: MarketingComponent,
                        providers: [
                            provideState(LeadSourceFeature),
                            provideEffects(
                                leadSourceEffects,
                            )
                        ],
                        data: {
                            permissions: FRONTEND_PERMISSIONS.marketing,
                            // TO DO: Write marketing help page
                            // helpId: 'marketing',
                        },
                    },
                    {
                        path: 'discounts',
                        component: DiscountsComponent,
                        data: {
                            permissions: FRONTEND_PERMISSIONS.marketing,
                            helpId: 'discounts',
                        },
                    },
                    {
                        path: 'settings',
                        component: ConfigComponent,
                        canActivate: [RoutePermissionGuard],
                        data: {
                            permissions: FRONTEND_PERMISSIONS.config,
                        },
                    },
                    {
                        path: 'accounting',
                        component: AccountingComponent,
                        data: {
                            permissions: FRONTEND_PERMISSIONS.settings,
                        }
                    },
                    {
                        path: 'payment',
                        component: PaymentTypesComponent,
                        // Requirement: BlueSage settings state and effects are scoped to
                        // the payment route so they are created when the panel opens and
                        // destroyed when it leaves, keeping credential inputs out of the
                        // long-lived root state.
                        providers: [
                            provideState(blueSageSettingsFeature),
                            provideEffects(blueSageSettingsEffects),
                        ],
                        canActivate: [RoutePermissionGuard],
                        data: {
                            permissions: FRONTEND_PERMISSIONS.settings,
                            // TO DO: Write payment types help page
                            // helpId: 'payment',
                        },
                    },
                    {
                        // Requirement: K8 - the review queue is an OPERATIONAL
                        // surface, not an admin one; it sits beside the payment
                        // settings only because that is where BlueSage lives
                        // today, and MoveRight own where it finally lands.
                        // K8 review queue. Placed beside the payment settings only
                        // because that is where BlueSage lives today — this is an
                        // operational surface, not an admin one, and MoveRight own
                        // where it finally sits. Moving it is this block and the
                        // nav entry; the component and its state travel with it,
                        // and the badge count comes from its own query so it can be
                        // surfaced anywhere independently of this path.
                        path: 'unmatched-payments',
                        component: BlueSageQueueComponent,
                        providers: [
                            provideState(blueSageQueueFeature),
                            provideEffects(blueSageQueueEffects),
                        ],
                        canActivate: [RoutePermissionGuard],
                        data: {
                            permissions: FRONTEND_PERMISSIONS.settings,
                        },
                    },
                    {
                        path: 'communications',
                        component: CommunicationsComponent,
                        data: {
                            permissions: FRONTEND_PERMISSIONS.settings,
                            // TO DO: Write settings help page
                            // helpId: 'settings',
                        },
                    },
                    {
                        path: 'history',
                        component: HistorySearchComponent,
                        data: {
                            permissions: FRONTEND_PERMISSIONS.settings,
                            // TO DO: Write payment types help page
                            // helpId: 'payment',
                        },
                    },
                    {
                         path: 'templates',
                         component: TemplatesComponent,
                         data: {
                             permissions: FRONTEND_PERMISSIONS.settings,
                             // TO DO: Write payment types help page
                             // helpId: 'payment',
                         },
                         providers:[
                             provideEffects(
                                 templatesEffects
                             )
                         ]
                     },
                     {
                         path: 'prophet',
                         loadComponent: () => import('./franchise/prophet/prophet.component').then(m => m.ProphetComponent),
                         data: {
                             permissions: FRONTEND_PERMISSIONS.settings,
                             helpId: 'business',
                             hiddenIfZoneType: [ZONE_TYPES.franchise, ZONE_TYPES.area],
                         },
                     },
                     {
                         path: 'tags',
                         loadComponent: () => import('./franchise/tags-manager/tags-manager.component').then(m => m.TagsManagerComponent),
                         data: {
                             permissions: FRONTEND_PERMISSIONS.settings,
                             helpId: 'business',
                         },
                     },
                     {
                         path: 'nicejobs',
                         redirectTo: '/integration/nicejobs',
                         pathMatch: 'full',
                     },
                     {
                         path: 'acton',
                         redirectTo: '/integration/acton',
                         pathMatch: 'full',
                     },
                     {
                         path: 'quickbooks',
                         redirectTo: '/integration/quickbooks',
                         pathMatch: 'full',
                     },
                     {
                         path: 'yelp',
                         redirectTo: '/integration/yelp',
                         pathMatch: 'full',
                     },
                     {
                         path: 'ringcentral',
                         redirectTo: '/integration/ringcentral',
                         pathMatch: 'full',
                     },
                 ]
             },
            {
                path: 'users',
                redirectTo: 'people',
                pathMatch: 'full'
            },
            {
                path: 'users/:sub',
                redirectTo: 'people/:sub'
            },
            {
                path: 'user/:id/:tab',
                component: UserPageComponent,
                canDeactivate: [UploadGuard],
                canActivate: [RoutePermissionGuard],
                data: { permissions: FRONTEND_PERMISSIONS.users, customTitle: 'from-component', helpId: 'users' },
                providers: [
                    provideState(userPageSimilarCustomersFeature),
                    provideState(userPageMergeConflictDialogFeature),
                    provideEffects(userPageSimilarCustomersEffects),
                    provideEffects(userPageMergeConflictDialogEffects),
                ],
            },
            {
                path: 'user/:id',
                redirectTo: 'user/:id/overview'
            },
            {
                path: 'people/:id/:tab',
                component: UserPageComponent,
                canDeactivate: [UploadGuard],
                canActivate: [RoutePermissionGuard],
                data: { permissions: FRONTEND_PERMISSIONS.users, customTitle: 'from-component', helpId: 'users' }
            },
            {
                path: 'people/:id',
                redirectTo: 'people/:id/overview'
            },
            {
                path: 'integration/nicejobs',
                component: NiceJobsComponent,
                canActivate: [RoutePermissionGuard],
                data: { permissions: FRONTEND_PERMISSIONS.settings },
            },
            {
                path: 'integration/acton',
                component: ActOnComponent,
                canActivate: [RoutePermissionGuard],
                data: { permissions: FRONTEND_PERMISSIONS.settings },
            },
            {
                path: 'integration/quickbooks',
                component: QuickbooksComponent,
                canActivate: [RoutePermissionGuard],
                data: { permissions: FRONTEND_PERMISSIONS.settings },
                children: [
                    {
                        path: 'login',
                        component: QuickbooksLoginComponent,
                    },
                    {
                        path: '',
                        component: QuickbooksDashboardComponent,
                    }
                ]
            },
            {
                path: 'integration/yelp',
                component: YelpComponent,
                canActivate: [RoutePermissionGuard],
                data: { permissions: FRONTEND_PERMISSIONS.settings },
            },
            {
                path: 'integration/ringcentral',
                component: RingcentralComponent,
                canActivate: [RoutePermissionGuard],
                data: { permissions: FRONTEND_PERMISSIONS.settings },
            },
            {
                path: 'pages',
                // pathMatch: 'prefix',
                component: PageComponent,
                children: [
                    {
                        path: '**',
                        component: PageComponent,
                    }
                ]
            },
            // Catch-all inside the app shell — renders 404 WITH sidebar/topbar
            { path: '**', component: AppNotfoundComponent },
        ],
    },

    { path: 'payment/redirect', component: PaymentRedirectComponent },
    { path: 'payment/:transaction', component: PaymentComponent },

    { path: 'error', component: AppErrorComponent },
    { path: 'access', component: AppAccessdeniedComponent },
    { path: 'notfound', component: AppNotfoundComponent },
    { path: 'no-available-zones', component: NoAvailableZonesComponent },

    // Redirects
    { path: 'login', redirectTo: '/auth/login' },

    // Don't redirect — preserve the original URL so AppNotfoundComponent can
    // read it and embed the corresponding Astro marketing page in an iframe.
    { path: '**', component: AppNotfoundComponent },
];


@NgModule({
    imports: [
         RouterModule.forRoot(routes, {
          scrollPositionRestoration: 'enabled',
          anchorScrolling: 'enabled',
          paramsInheritanceStrategy: 'always',
        }),
    ],
    exports: [RouterModule]
})
export class AppRoutingModule {
}
